import React, { useEffect, useRef, useState } from 'react';
import {
  FileText,
  Calendar,
  Clock,
  Award,
  CheckCircle2,
  Loader2,
  AlertCircle,
  PlayCircle,
  Upload,
  X,
  Paperclip,
  File,
  Download,
  ChevronDown,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import {
  useModuleDetailsQuery,
  useCourseAssignmentsQuery,
  useModuleCompletionQuery,
  useMarkModuleCompleteMutation,
  useAssignmentStatusQuery,
  CourseModule,
} from '../../../api/studentCoursesApi';
import { tokenService } from '../../../services/TokenService';
import { useNavigate } from 'react-router-dom';
import routes from '../../../routes';
import '../page/PageModuleStyles.css';

interface AssignModuleContentProps {
  contextId: string;
  moduleId: string;
  module: CourseModule | null;
  headerColor?: string;
}

function computeTimeRemaining(dueDate: Date): string {
  const diff = dueDate.getTime() - Date.now();
  if (diff <= 0) return 'Overdue';
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  if (days > 0) return `${days}d ${hours}h remaining`;
  if (hours > 0) return `${hours}h ${mins}m remaining`;
  return `${mins}m remaining`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const AssignModuleContent: React.FC<AssignModuleContentProps> = ({
  contextId,
  moduleId,
  module,
  headerColor,
}) => {
  const token = tokenService.getToken();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGradeModal, setShowGradeModal] = useState(false);
  // accordion open state: key â†’ boolean
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    description: true,
    activity: false,
    project: false,
    submission: true,
  });
  const toggleSection = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const { data: details, isLoading } = useModuleDetailsQuery(
    contextId,
    moduleId,
    'assign',
    token,
    { enabled: !!contextId && !!moduleId },
  );

  const { data: courseAssignments } = useCourseAssignmentsQuery(contextId, token);

  const { data: completionData, refetch: refetchCompletion } =
    useModuleCompletionQuery(contextId, moduleId, token);

  const { data: assignStatus } = useAssignmentStatusQuery(
    contextId,
    details?.instance,
    token,
  );

  const markComplete = useMarkModuleCompleteMutation(contextId);
  const isCompleted = completionData?.completed ?? false;
  const isGraded = assignStatus?.graded ?? false;

  // Auto-mark complete when instructor grades the assignment
  useEffect(() => {
    if (!isGraded || isCompleted) return;

    // Step 1: Re-read from Moodle — automatic completion mode already marks it
    // complete server-side the moment it is graded.
    refetchCompletion().then(({ data }) => {
      // Step 2: Manual completion mode — Moodle still shows incomplete, so we
      // push the completion ourselves. Errors are swallowed because some Moodle
      // setups disallow manual completion for graded activities.
      if (!data?.completed) {
        markComplete.mutate(
          { moduleId },
          {
            onSuccess: () => refetchCompletion(),
            onError: () => { /* silently ignore */ },
          },
        );
      }
    });
  }, [isGraded, isCompleted]); // eslint-disable-line react-hooks/exhaustive-deps

  const assignment = courseAssignments?.assignments.find(
    (a) => a.name === module?.name,
  );

  const dueDate = assignment?.dueDate ? new Date(assignment.dueDate) : null;
  const isOverdue = !!dueDate && dueDate < new Date() && !isCompleted;
  const isDueSoon =
    !!dueDate &&
    dueDate > new Date() &&
    dueDate.getTime() - Date.now() < 3 * 24 * 60 * 60 * 1000;

  const timeRemaining = dueDate ? computeTimeRemaining(dueDate) : null;
  const maxGrade = assignment?.maxGrade ?? 0;

  const assignDescription = details?.intro || module?.description || assignment?.intro || '';
  const activityInstructions = details?.activityInstructions || '';

  const timeRemainingDisplay = (() => {
    if (!dueDate) return null;
    if (assignStatus?.submissionStatus === 'submitted' && assignStatus?.submittedAt) {
      const diff = dueDate.getTime() - new Date(assignStatus.submittedAt).getTime();
      if (diff > 0) {
        const days = Math.floor(diff / 86_400_000);
        const hours = Math.floor((diff % 86_400_000) / 3_600_000);
        const parts: string[] = [];
        if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
        if (hours > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
        return `Assignment was submitted ${parts.join(' ')} early`;
      }
      return 'Assignment was submitted on time';
    }
    return timeRemaining;
  })();

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const merged = [...files];
    Array.from(incoming).forEach((f) => {
      if (!merged.some((e) => e.name === f.name && e.size === f.size)) merged.push(f);
    });
    setFiles(merged);
    setIsSubmitted(false);
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setIsSubmitted(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const handleSubmit = () => {
    if (!files.length) return;
    setIsSubmitting(true);
    setTimeout(() => { setIsSubmitting(false); setIsSubmitted(true); }, 900);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 flex justify-center items-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // â”€â”€ Accordion section helper â”€â”€
  const Accordion = ({
    id, label, icon, count, children,
  }: {
    id: string;
    label: string;
    icon: React.ReactNode;
    count?: number;
    children: React.ReactNode;
  }) => {
    const open = openSections[id];
    return (
      <div className="border-t border-gray-100">
        <button
          type="button"
          onClick={() => toggleSection(id)}
          className="w-full flex items-center justify-between px-6 py-3.5 hover:bg-gray-50 transition-colors text-left"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            {icon}
            {label}
            {count !== undefined && (
              <span className="ml-1 text-xs font-bold bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5">
                {count}
              </span>
            )}
          </span>
          {open
            ? <ChevronDown className="h-4 w-4 text-gray-400" />
            : <ChevronRight className="h-4 w-4 text-gray-400" />
          }
        </button>
        {open && <div className="px-6 pb-5 pt-1">{children}</div>}
      </div>
    );
  };

  return (
    <>
      {/* â”€â”€ Grade Details Modal â”€â”€ */}
      {showGradeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowGradeModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          {/* Dialog */}
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="bg-linear-to-r from-green-500 to-emerald-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 rounded-lg p-1.5">
                  <Award className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold text-green-100 uppercase tracking-widest">Feedback</p>
                  <p className="text-base font-bold text-white">{module?.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowGradeModal(false)}
                className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Grade badge */}
            <div className="px-6 pt-5 pb-3 flex items-center justify-between bg-green-50 border-b border-green-100">
              <span className="text-sm font-semibold text-gray-600">Grade</span>
              <span className="text-2xl font-extrabold text-green-700">
                {assignStatus?.gradeForDisplay
                  ? assignStatus.gradeForDisplay
                  : assignStatus?.grade != null
                    ? `${assignStatus.grade}${assignStatus.maxGrade ? ` / ${assignStatus.maxGrade}` : ''}`
                    : 'â€”'}
              </span>
            </div>

            {/* Modal rows */}
            <div className="divide-y divide-gray-100">
              {assignStatus?.gradedDate && (
                <div className="flex items-start gap-3 px-6 py-3.5">
                  <Calendar className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-0.5">Graded on</p>
                    <p className="text-sm text-gray-800">
                      {new Date(assignStatus.gradedDate).toLocaleDateString('en-US', {
                        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                      })}
                      {', '}
                      {new Date(assignStatus.gradedDate).toLocaleTimeString('en-US', {
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              )}

              <div className="px-6 py-3.5">
                <p className="text-xs font-semibold text-gray-500 mb-1.5">Feedback comments</p>
                {assignStatus?.feedbackComments ? (
                  <div
                    className="moodle-content text-sm text-gray-700"
                    dangerouslySetInnerHTML={{ __html: assignStatus.feedbackComments }}
                  />
                ) : (
                  <p className="text-sm text-gray-400 italic">Nothing to display</p>
                )}
              </div>

              {/* Submitted files in modal */}
              {assignStatus && assignStatus.submittedFiles.length > 0 && (
                <div className="px-6 py-3.5">
                  <p className="text-xs font-semibold text-gray-500 mb-1.5">Submitted files</p>
                  <div className="space-y-1.5">
                    {assignStatus.submittedFiles.map((file, idx) => (
                      <a
                        key={idx}
                        href={file.fileurl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline text-sm"
                      >
                        <File className="h-3.5 w-3.5 shrink-0" />
                        {file.filename}
                        <span className="text-gray-400 text-xs ml-auto">{formatBytes(file.filesize)}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 flex justify-end">
              <Button size="sm" onClick={() => setShowGradeModal(false)} className="bg-gray-700 hover:bg-gray-800">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-h-[calc(100vh-7.8rem)] overflow-y-auto">
        {/* â”€â”€ Header â”€â”€ */}
        <div className="px-6 py-5 overflow-hidden rounded-t-xl" style={{ background: '#F9FAFB', borderTop: `4px solid ${headerColor || '#374151'}` }}>
          <div className="flex items-start gap-4">
            <div className="rounded-lg p-2.5 shrink-0" style={{ background: headerColor ? `${headerColor}1A` : '#E5E7EB' }}>
              <FileText className="h-6 w-6" style={{ color: headerColor || '#374151' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-1">
                Assignment
              </p>
              <h2 className="text-xl font-bold leading-snug text-gray-900">{module?.name}</h2>
              {maxGrade > 0 && (
                <div className="flex items-center gap-1.5 mt-2">
                  <Award className="h-4 w-4 text-yellow-500 shrink-0" />
                  <span className="text-sm text-gray-600">Maximum grade: {maxGrade}</span>
                </div>
              )}
            </div>
            {(isCompleted || isGraded || isSubmitted) && (
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full shrink-0 border ${
                isGraded
                  ? 'bg-yellow-100 border-yellow-300'
                  : 'bg-green-100 border-green-300'
              }`}>
                {isGraded
                  ? <Award className="h-4 w-4 text-yellow-600" />
                  : <CheckCircle2 className="h-4 w-4 text-green-600" />
                }
                <span className="text-xs font-semibold text-gray-700">
                  {isCompleted ? 'Completed' : isGraded ? 'Graded' : 'Submitted'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* â”€â”€ Body â”€â”€ */}
        <div className="bg-white border border-t-0 border-gray-200 rounded-b-xl">

          {/* ── Status cards ── */}
          <div className="p-5 bg-white grid grid-cols-2 gap-3">

            {/* Submission */}
            <div className={`rounded-xl p-4 border flex flex-col gap-2 ${
              isCompleted || isGraded || assignStatus?.submissionStatus === 'submitted'
                ? 'bg-green-50 border-green-200'
                : isOverdue ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1">
                <Upload className="h-3 w-3" /> Submission
              </p>
              {isCompleted || isGraded || assignStatus?.submissionStatus === 'submitted' ? (
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                  <span className="text-sm font-semibold text-green-700">Submitted for grading</span>
                </div>
              ) : isSubmitted ? (
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-blue-500 shrink-0" />
                  <span className="text-sm font-semibold text-blue-700">Awaiting grading</span>
                </div>
              ) : isOverdue ? (
                <div className="flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                  <span className="text-sm font-semibold text-red-700">No submission</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-amber-500 shrink-0" />
                  <span className="text-sm font-semibold text-amber-700">No submission yet</span>
                </div>
              )}
            </div>

            {/* Grade */}
            <div className={`rounded-xl p-4 border flex flex-col gap-2 ${
              isCompleted || isGraded ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1">
                <Award className="h-3 w-3" /> Grade
              </p>
              {isCompleted || isGraded ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-2xl font-extrabold text-green-700 leading-none">
                    {assignStatus?.gradeForDisplay || (assignStatus?.grade != null ? `${assignStatus.grade}${assignStatus.maxGrade ? ` / ${assignStatus.maxGrade}` : ''}` : '-')}
                  </span>
                  <button
                    onClick={() => setShowGradeModal(true)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:text-green-900 bg-green-100 hover:bg-green-200 border border-green-200 rounded-lg px-2.5 py-1 transition-colors shrink-0"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Feedback
                  </button>
                </div>
              ) : (
                <span className="text-sm font-medium text-gray-400">Not graded yet</span>
              )}
            </div>

            {/* Due date */}
            {dueDate && (
              <div className={`rounded-xl p-4 border flex flex-col gap-2 ${isOverdue ? 'bg-red-50 border-red-200' : isDueSoon ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Due date
                </p>
                <span className={`text-sm font-semibold ${isOverdue ? 'text-red-700' : isDueSoon ? 'text-amber-700' : 'text-gray-800'}`}>
                  {dueDate.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  {', '}
                  {dueDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            )}

            {/* Time */}
            {timeRemainingDisplay && (
              <div className={`rounded-xl p-4 border flex flex-col gap-2 ${
                assignStatus?.submissionStatus === 'submitted' || isGraded
                  ? 'bg-green-50 border-green-200'
                  : isOverdue ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
              }`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Time
                </p>
                <span className={`text-sm font-semibold ${
                  assignStatus?.submissionStatus === 'submitted' || isGraded ? 'text-green-700' : isOverdue ? 'text-red-700' : 'text-gray-800'
                }`}>
                  {timeRemainingDisplay}
                </span>
              </div>
            )}

            {/* Last modified */}
            {assignStatus?.lastModified && (
              <div className="rounded-xl p-4 border border-gray-200 bg-gray-50 flex flex-col gap-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Last modified
                </p>
                <span className="text-sm font-semibold text-gray-800">
                  {new Date(assignStatus.lastModified).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  {', '}
                  {new Date(assignStatus.lastModified).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            )}

            {/* Opened */}
            {assignment?.allowSubmissionsFromDate && (
              <div className="rounded-xl p-4 border border-gray-200 bg-gray-50 flex flex-col gap-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Opened
                </p>
                <span className="text-sm font-semibold text-gray-800">
                  {new Date(assignment.allowSubmissionsFromDate).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>
            )}

          </div>

          {/* Submitted files */}
          {assignStatus && assignStatus.submittedFiles.length > 0 && (
            <div className="px-5 pb-5 bg-white border-t border-gray-100">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 py-3 flex items-center gap-1.5">
                <Paperclip className="h-3 w-3" /> Submitted files
              </p>
              <div className="space-y-1.5">
                {assignStatus.submittedFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200 hover:border-blue-200 hover:bg-blue-50 transition-colors group">
                    <File className="h-4 w-4 text-gray-400 shrink-0" />
                    <a
                      href={file.fileurl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm font-medium flex-1 truncate"
                    >
                      {file.filename}
                    </a>
                    <span className="text-gray-400 text-xs shrink-0">{formatBytes(file.filesize)}</span>
                    {file.timemodified && (
                      <span className="text-gray-400 text-xs shrink-0 hidden sm:block">
                        {new Date(file.timemodified).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    <a href={file.fileurl} download className="p-1 rounded-md text-gray-300 group-hover:text-blue-500 transition-colors shrink-0">
                      <Download className="h-3.5 w-3.5" />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}


          {/* â•â• ACCORDION: Assignment description â•â• */}
          {assignDescription && (
            <Accordion
              id="description"
              label="Assignment description"
              icon={<FileText className="h-4 w-4 text-gray-400" />}
            >
              <div
                className="moodle-content text-sm text-gray-700"
                dangerouslySetInnerHTML={{ __html: assignDescription }}
              />
            </Accordion>
          )}

          {/* â•â• ACCORDION: Activity instructions â•â• */}
          {activityInstructions && (
            <Accordion
              id="activity"
              label="Activity instructions"
              icon={<FileText className="h-4 w-4 text-indigo-400" />}
            >
              <div
                className="moodle-content text-sm text-gray-700"
                dangerouslySetInnerHTML={{ __html: activityInstructions }}
              />
            </Accordion>
          )}

          {/* â•â• ACCORDION: Linked project â•â• */}
          {module?.linkedProject && (
            <Accordion
              id="project"
              label="Your project"
              icon={<PlayCircle className="h-4 w-4 text-blue-500" />}
            >
              <div className="bg-linear-to-br from-blue-50 via-indigo-50 to-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-3 min-w-0">
                    <div>
                      <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-0.5">Linked Project</p>
                      <p className="text-base font-bold text-gray-900 truncate">{module.linkedProject.title}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-full text-xs font-medium text-gray-700 border border-gray-200 shadow-sm">
                        <Award className="h-3 w-3 text-blue-500" />
                        {module.linkedProject.difficultyLevel}
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-full text-xs font-medium text-gray-700 border border-gray-200 shadow-sm">
                        <Award className="h-3 w-3 text-yellow-500" />
                        {module.linkedProject.totalPoints} pts
                      </span>
                    </div>
                    {module.linkedProject.progress && (
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <div className="flex justify-between text-xs mb-1.5">
                          <span className="text-gray-500 font-medium">Your Progress</span>
                          <span className="font-bold text-blue-600">{Math.round(module.linkedProject.progress.percentage)}%</span>
                        </div>
                        <Progress value={module.linkedProject.progress.percentage} className="h-2" />
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={() => navigate(routes.toProjectDetails(module.linkedProject!.id))}
                    className="shrink-0 bg-blue-600 hover:bg-blue-700"
                    size="sm"
                  >
                    <PlayCircle className="h-3.5 w-3.5 mr-1.5" />
                    Open
                  </Button>
                </div>
              </div>
            </Accordion>
          )}

          {/* â•â• ACCORDION: Add submission (hidden once graded) â•â• */}
          {!isCompleted && !isGraded && (
            <Accordion
              id="submission"
              label="Add submission"
              icon={<Upload className="h-4 w-4 text-gray-400" />}
            >
              {/* Drop zone */}
              {!isSubmitted && (
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`cursor-pointer border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                    isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                  }`}
                >
                  <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
                  <div className="flex flex-col items-center gap-2">
                    <div className={`rounded-full p-3 ${isDragOver ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <Upload className={`h-6 w-6 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">
                        {isDragOver ? 'Drop files here' : 'Drag & drop files or click to browse'}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">Any file type accepted</p>
                    </div>
                  </div>
                </div>
              )}

              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((file, idx) => (
                    <div key={idx} className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${isSubmitted ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <Paperclip className={`h-4 w-4 shrink-0 ${isSubmitted ? 'text-green-500' : 'text-gray-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                        <p className="text-xs text-gray-400">{formatBytes(file.size)}</p>
                      </div>
                      {isSubmitted ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); removeFile(idx); }} className="p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {files.length > 0 && !isSubmitted && (
                <div className="mt-4 flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-400">{files.length} file{files.length > 1 ? 's' : ''} ready to submit</p>
                  <Button size="sm" onClick={handleSubmit} disabled={isSubmitting} className="bg-indigo-600 hover:bg-indigo-700">
                    {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                    {isSubmitting ? 'Submittingâ€¦' : 'Submit Assignment'}
                  </Button>
                </div>
              )}

              {isSubmitted && (
                <div className="mt-3 flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                  <span className="text-sm font-medium text-green-800">Assignment submitted successfully</span>
                </div>
              )}
            </Accordion>
          )}

          {/* â”€â”€ Footer â”€â”€ */}
          <div className="sticky bottom-0 z-10 px-6 py-4 bg-gray-50 rounded-b-xl border-t border-gray-100 flex items-center justify-between gap-3">
            {isCompleted || isGraded ? (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                <span className="text-xs font-medium text-green-800">Completed</span>
              </div>
            ) : (
              <Button
                size="sm"
                onClick={() => markComplete.mutate({ moduleId }, { onSuccess: () => refetchCompletion() })}
                disabled={(!isSubmitted && !isGraded) || markComplete.isPending}
                title={(!isSubmitted && !isGraded) ? 'Submit your assignment first' : undefined}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {markComplete.isPending
                  ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  : <CheckCircle2 className="h-4 w-4 mr-1.5" />
                }
                Mark as Complete
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignModuleContent;

